import {DEFAULT_FONT} from '@pinecast/common/constants';
import {useDialogIsolation} from '@pinecast/common/hooks/useDialogIsolation';
import Layer from '@pinecast/common/Layer';
import {useIntl} from '@pinecast/i18n';
import type {ColorMap} from '@pinecast/styles';
import styled, {focusVisibleRing} from '@pinecast/styles';
import {REDUCED_MOTION} from '@pinecast/styles/a11y';
import * as React from 'react';

/**
 * A dashboard navigation on a phone: a drawer that rises from the bottom of
 * the screen with the navigation's items in it (`narrow-nav-select`, prompt
 * U2).
 *
 * It replaces a `<select>` that had no name and changed the page on every
 * change, so arrowing through it moved a keyboard user to another page (WCAG
 * 3.2.2 and 4.1.2). Nothing here changes the page until an item is chosen.
 *
 * It is a modal dialog, isolated from the page by `useDialogIsolation` like
 * every other dialog in the dashboard: the page behind it is inert, focus
 * moves to its title when it opens, Escape closes it, and focus goes back to
 * the button that opened it. Mount it only while it is open.
 */

const slideUp = {
  from: {transform: 'translateY(100%)'},
  to: {transform: 'translateY(0)'},
};

const Backdrop = styled('div', {
  backgroundColor: 'rgba(0, 0, 0, 0.6)',
  bottom: 0,
  display: 'flex',
  flexDirection: 'column',
  justifyContent: 'flex-end',
  left: 0,
  position: 'fixed',
  right: 0,
  top: 0,
  zIndex: 100,
});

const Sheet = styled('div', (_: unknown, designColors) => ({
  animationDuration: '0.2s',
  animationName: slideUp,
  animationTimingFunction: 'ease-out',
  backgroundColor: designColors.contentBackground,
  borderTopLeftRadius: 12,
  borderTopRightRadius: 12,
  boxShadow: '0 -8px 24px rgba(0, 0, 0, 0.2)',
  boxSizing: 'border-box',
  color: designColors.contentText,
  maxHeight: '85%',
  overflowY: 'auto',
  overscrollBehavior: 'contain',
  padding: '8px 12px calc(24px + env(safe-area-inset-bottom))',
  [REDUCED_MOTION]: {animationName: 'none'},
}));

const Header = styled('div', {
  alignItems: 'center',
  display: 'flex',
  gap: 12,
  justifyContent: 'space-between',
  padding: '4px 0 8px 12px',
});

const Title = styled('h2', {
  fontFamily: 'Monument Grotesk Bold',
  fontSize: 18,
  fontWeight: 400,
  lineHeight: '24px',
  margin: 0,
});

const CloseButton = styled(
  'button',
  (_: unknown, designColors) => ({
    alignItems: 'center',
    background: 'none',
    border: 0,
    borderRadius: 22,
    color: designColors.contentText,
    cursor: 'pointer',
    display: 'inline-flex',
    flex: '0 0 auto',
    height: 44,
    justifyContent: 'center',
    margin: 0,
    padding: 0,
    width: 44,
    ...focusVisibleRing(designColors, -2),
  }),
  {type: 'button'},
);

/** A `ul` for the items, with its list semantics kept (see AGENTS.md). */
export const NavDrawerList = styled(
  'ul',
  {listStyle: 'none', margin: 0, padding: 0},
  {role: 'list'},
);

/** The look of an item, a link or a button, in the drawer. */
export const navDrawerItemStyles = (
  current: boolean,
  designColors: ColorMap,
) => ({
  alignItems: 'center',
  background: current ? designColors.accentBlue : 'none',
  border: 0,
  borderRadius: '4px',
  boxSizing: 'border-box' as const,
  color: current ? designColors.accentBlueText : designColors.contentText,
  cursor: 'pointer',
  display: 'flex',
  fontFamily: DEFAULT_FONT,
  fontSize: '16px',
  fontWeight: current ? 500 : 400,
  gap: '8px',
  lineHeight: '20px',
  margin: 0,
  minHeight: '48px',
  padding: '14px 12px',
  textAlign: 'left' as const,
  textDecoration: 'none',
  width: '100%',
  ':hover': {textDecoration: current ? 'none' : 'underline'},
  // Outside the item, so it shows around the current item's fill.
  ...focusVisibleRing(designColors),
});

// A filled row rather than an outlined box, which read as a text field.
const ToggleButton = styled(
  'button',
  (_: unknown, designColors) => ({
    alignItems: 'center',
    background: designColors.badgeGrayBackground,
    border: 0,
    borderRadius: 6,
    boxSizing: 'border-box',
    color: designColors.contentText,
    cursor: 'pointer',
    display: 'flex',
    fontFamily: DEFAULT_FONT,
    gap: 12,
    // Longhands, since pages set `marginBottom` through `style`.
    marginBottom: 0,
    marginLeft: 0,
    marginRight: 0,
    marginTop: 0,
    minHeight: 44,
    padding: '8px 14px',
    textAlign: 'left',
    width: '100%',
    ...focusVisibleRing(designColors),
  }),
  {type: 'button'},
);
const ToggleText = styled('span', {
  display: 'flex',
  flexDirection: 'column',
  minWidth: 0,
});
// The drawer's name, which keeps the button named by what it opens.
const ToggleLabel = styled('span', (_: unknown, designColors) => ({
  color: designColors.badgeGrayText,
  fontSize: 12,
  fontWeight: 600,
  letterSpacing: '0.08em',
  lineHeight: '16px',
  textTransform: 'uppercase',
}));
// Between the drawer's name and the current item, so that the button's name
// keeps them apart ("Settings menu Podcast details"). A flex container does
// not lay out a space between its items, so it does not show.
const NAME_SEPARATOR = ' ';
const ToggleCurrent = styled('span', {
  fontSize: 15,
  fontWeight: 500,
  lineHeight: '20px',
});

const MenuIcon = () => (
  <svg aria-hidden="true" height={16} viewBox="0 0 16 16" width={16}>
    <path
      d="M2 4h12M2 8h12M2 12h12"
      fill="none"
      stroke="currentColor"
      strokeLinecap="round"
      strokeWidth={1.75}
    />
  </svg>
);

/**
 * A full-width button that opens a drawer: the second level of navigation on
 * a settings page, or the first at an address that no link names, where there
 * is no heading to hold the button. It shows the drawer's name, which names the
 * button, above the item that is current, since the items are otherwise only
 * visible in the drawer.
 */
export const NavDrawerButton = ({
  current,
  expanded,
  label,
  onClick,
  style,
}: {
  current?: React.ReactNode;
  expanded: boolean;
  label: string;
  onClick: () => void;
  style?: React.ComponentProps<typeof ToggleButton>['style'];
}) => (
  <ToggleButton
    aria-expanded={expanded}
    aria-haspopup="dialog"
    onClick={onClick}
    style={style}
  >
    <MenuIcon />
    <ToggleText>
      <ToggleLabel>{label}</ToggleLabel>
      {current && NAME_SEPARATOR}
      {current && <ToggleCurrent>{current}</ToggleCurrent>}
    </ToggleText>
  </ToggleButton>
);

type Props = {
  /** The items. `close` closes the drawer; call it once an item is chosen. */
  children: (close: () => void) => React.ReactNode;
  /** The drawer's title, which also names the `<nav>` inside it. */
  label: string;
  onClose: () => void;
};

const NavDrawer = ({children, label, onClose}: Props) => {
  const intl = useIntl();
  const ref = useDialogIsolation<HTMLDivElement>({onEscape: onClose});
  const titleID = React.useId();
  return (
    <Layer position="fixed">
      <Backdrop
        onClick={(e: React.MouseEvent) => {
          if (e.target === e.currentTarget) {
            onClose();
          }
        }}
        ref={ref}
      >
        <Sheet aria-labelledby={titleID} aria-modal="true" role="dialog">
          <Header>
            <Title id={titleID}>{label}</Title>
            <CloseButton
              aria-label={intl.formatMessage({
                description: 'Close button',
                defaultMessage: 'Close',
              })}
              onClick={onClose}
            >
              <svg
                aria-hidden="true"
                height={18}
                viewBox="0 0 18 18"
                width={18}
              >
                <path
                  d="M4 4l10 10M14 4L4 14"
                  fill="none"
                  stroke="currentColor"
                  strokeLinecap="round"
                  strokeWidth={1.75}
                />
              </svg>
            </CloseButton>
          </Header>
          <nav aria-labelledby={titleID}>{children(onClose)}</nav>
        </Sheet>
      </Backdrop>
    </Layer>
  );
};

export default NavDrawer;
